// @@PORTAL_PAY_v124@@ HQ › Collections › Portal payments — what clients paid (or reported) through the portal and
// whether it's recorded in Zoho Books. Admin/manager only (server-gated).
// @@PORTAL_PAY_v124_1@@ Payment settings moved to the Settings page: named export PaymentSettingsPanel (inline panel,
// anchor #portal-payments) — card switch, fee %, bank details, Wise link, check instructions, fee-exempt clients,
// Zoho Payments connection test. Check payments ('check' method) show here alongside bank transfers.
// @@PORTAL_PAY_v124_2@@ Wise reports ('wise' method) are listed separately from ACH bank transfers.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

const STATUS = {
  pending: ['Checkout open', 'muted'], paid: ['Paid · not in Books', 'amber'], recorded: ['Recorded', 'green'],
  record_failed: ['Paid · Books failed', 'red'], failed: ['Failed', 'muted'], expired: ['Abandoned', 'muted'], reported: ['Transfer reported', 'amber'],
};
const statusOf = (p) => (p.status === 'reported' && p.method === 'check' ? ['Check mailed', 'amber']
  : p.status === 'reported' && p.method === 'wise' ? ['Wise reported', 'amber'] : STATUS[p.status] || [p.status, 'muted']);
function money(n, ccy) {
  if (n == null) return '—';
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: ccy || 'USD' }).format(n); }
  catch (e) { return '$' + Number(n).toFixed(2); }
}
function stamp(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch (e) { return ''; }
}
async function send(path, method, body) {
  const r = await apiFetch(path, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body || {}) });
  const j = await r.json().catch(() => ({}));
  return { r, j };
}

export function PaymentSettingsPanel() {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [clients, setClients] = useState([]);
  const [add, setAdd] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [test, setTest] = useState(null);
  const load = useCallback(async () => {
    const r = await apiFetch('/api/portal-payments/settings');
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { setD({ error: j.error || 'Could not load settings.' }); return; }
    setD(j); setForm({ ...j.settings, card_fee_pct: String(j.settings.card_fee_pct) });
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiFetch('/api/clients').then((r) => (r.ok ? r.json() : null)).then((j) => setClients(((j && j.clients) || []).map((c) => ({ id: c.id, name: c.name })))).catch(() => {});
  }, []);
  // Deep link from Collections (/settings#portal-payments): scroll once the panel has content.
  const loaded = !!d;
  useEffect(() => {
    if (loaded && window.location.hash === '#portal-payments') {
      const el = document.getElementById('portal-payments');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loaded]);

  async function save() {
    setBusy(true); setMsg(null);
    const { r, j } = await send('/api/portal-payments/settings', 'PUT', { ...form, card_fee_pct: Number(form.card_fee_pct) });
    setBusy(false);
    if (r.ok) { setMsg({ ok: true, text: 'Saved.' }); setForm({ ...j.settings, card_fee_pct: String(j.settings.card_fee_pct) }); }
    else setMsg({ ok: false, text: j.error || 'Could not save.' });
  }
  async function runTest() {
    setTest({ busy: true });
    const { r, j } = await send('/api/portal-payments/settings/test', 'POST', {});
    setTest(r.ok ? j : { ok: false, message: j.error || 'Test failed.' });
  }
  async function exempt(id, on) {
    const { r } = await send('/api/portal-payments/clients/' + id + '/fee-exempt', 'PUT', { exempt: on });
    if (r.ok) { setAdd(''); load(); }
  }
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const exemptIds = new Set(((d && d.exempt_clients) || []).map((c) => c.id));

  return (
    <div className="hq-panel" id="portal-payments" style={{ marginTop: 20, scrollMarginTop: 20 }}>
      <div className="hq-panel-head">
        <h2>Client portal payments</h2>
        <button type="button" className="hq-btn" disabled={busy || !form} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
      <div className="hq-v124-settings">
          {!d ? <p className="hq-v123-muted">Loading…</p> : d.error ? <p className="hq-v123-err">{d.error}</p> : (
            <>
              <span className="hq-modal-label">Card payments (Zoho Payments)</span>
              <label className="hq-v123-check">
                <input type="checkbox" checked={!!form.portal_card_enabled} disabled={busy || (!d.zoho_payments_configured && !form.portal_card_enabled)}
                  onChange={(e) => set('portal_card_enabled', e.target.checked)} />
                <span>Let clients pay invoices by card in the portal</span>
              </label>
              <p className="hq-modal-hint">
                {d.zoho_payments_configured ? 'Zoho Payments credentials are set on the server.' : 'Zoho Payments isn’t configured yet — set ZOHO_PAY_ACCOUNT_ID and ZOHO_PAY_API_KEY on the web service, and re-mint ZOHO_REFRESH_TOKEN with the ZohoPay scopes.'}
                {' '}{d.record_in_books ? 'Paid card payments are recorded in Zoho Books automatically.' : 'Automatic Books recording is OFF (PORTAL_PAY_RECORD_IN_BOOKS=0).'}
              </p>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button type="button" className="hq-btn hq-btn-ghost" disabled={busy || (test && test.busy)} onClick={runTest}>{test && test.busy ? 'Testing…' : 'Test Zoho Payments connection'}</button>
                {test && !test.busy && <span className={test.ok ? 'hq-v124-ok' : 'hq-v123-err'} style={{ margin: 0 }}>{test.message}</span>}
              </div>

              <span className="hq-modal-label">Credit card surcharge (%)</span>
              <input className="hq-input" type="number" step="0.01" min="0" max={d.fee_cap} style={{ width: 120 }} disabled={busy}
                value={form.card_fee_pct} onChange={(e) => set('card_fee_pct', e.target.value)} />
              <p className="hq-modal-hint">Capped at {d.fee_cap}% (card-network limit). Applied to credit cards only — debit/prepaid cards get the fee refunded automatically.</p>

              <span className="hq-modal-label">Bank transfer (ACH) instructions (shown to clients)</span>
              <textarea className="hq-input" rows={8} style={{ width: '100%', height: 'auto', resize: 'vertical', fontFamily: 'inherit' }} disabled={busy}
                placeholder={'Bank Name: …\nABA (Routing): …\nAccount Name: XYZ Merchandise LLC\nAccount #: …'}
                value={form.pay_bank_details} onChange={(e) => set('pay_bank_details', e.target.value)} />
              <p className="hq-modal-hint">Shown under “Bank transfer (ACH)”. Only logged-in portal clients see this (never the demo account). Leave empty to hide that option.</p>

              <span className="hq-modal-label">Wise payment link</span>
              <input className="hq-input" style={{ width: '100%' }} disabled={busy} placeholder="https://wise.com/pay/business/…"
                value={form.pay_wise_url} onChange={(e) => set('pay_wise_url', e.target.value)} />
              <p className="hq-modal-hint">Shown as its own “Pay with Wise” option. Leave empty to hide it.</p>

              <span className="hq-modal-label">Check instructions (shown to clients)</span>
              <textarea className="hq-input" rows={5} style={{ width: '100%', height: 'auto', resize: 'vertical', fontFamily: 'inherit' }} disabled={busy}
                placeholder={'Make checks payable to: XYZ Merchandise LLC\nMail to:\nXYZ Merchandise LLC\n…'}
                value={form.pay_check_details || ''} onChange={(e) => set('pay_check_details', e.target.value)} />
              <p className="hq-modal-hint">Leave empty to hide “Pay by check”. Clients are asked to write the invoice numbers on the memo line and can tell you when they’ve mailed it.</p>

              <span className="hq-modal-label">No card fee for</span>
              {(d.exempt_clients || []).length === 0 ? <p className="hq-v123-muted" style={{ margin: '4px 0' }}>Every client pays the card fee on credit cards.</p> : (
                <div className="hq-v124-chips">
                  {d.exempt_clients.map((c) => <span key={c.id} className="hq-v124-chip">{c.name}<button type="button" aria-label={'Remove ' + c.name} onClick={() => exempt(c.id, false)}>×</button></span>)}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <select className="hq-select" value={add} onChange={(e) => setAdd(e.target.value)} style={{ flex: 1 }}>
                  <option value="">Add a client…</option>
                  {clients.filter((c) => !exemptIds.has(c.id)).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <button type="button" className="hq-btn hq-btn-ghost" disabled={!add} onClick={() => exempt(add, true)}>Add</button>
              </div>
              {msg && <p className={msg.ok ? 'hq-v124-ok' : 'hq-v123-err'}>{msg.text}</p>}
              <div style={{ marginTop: 16 }}>
                <button type="button" className="hq-btn" disabled={busy || !form} onClick={save}>{busy ? 'Saving…' : 'Save payment settings'}</button>
              </div>
            </>
          )}
      </div>
    </div>
  );
}

export default function PortalPayments() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try {
      const r = await apiFetch('/api/portal-payments');
      const j = await r.json().catch(() => ({}));
      setData(r.ok ? j : { error: j.error || 'Could not load portal payments.', payments: [] });
    } catch (e) { setData({ error: 'Could not reach the server.', payments: [] }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function act(p, what) {
    setBusy(p.id + what); setErr('');
    const { r, j } = await send('/api/portal-payments/' + p.id + '/' + what, 'POST', {});
    setBusy('');
    if (!r.ok) { setErr(j.error || 'That didn’t work.'); return; }
    setData((d) => ({ ...d, payments: d.payments.map((x) => (x.id === p.id ? j.payment : x)) }));
  }

  const list = (data && data.payments) || [];
  const attention = list.filter((p) => ['paid', 'record_failed', 'reported'].includes(p.status) || p.error).length;
  return (
    <div className="hq-panel" style={{ marginTop: 20 }}>
      <div className="hq-panel-head">
        <h2>Portal payments{attention ? <span className="hq-v123-count">{attention} need attention</span> : null}</h2>
        <div className="hq-v123-headacts">
          <button className="hq-btn hq-btn-ghost" onClick={load}>Refresh</button>
          <Link className="hq-btn hq-btn-ghost hq-v124-link" to="/settings#portal-payments">Payment settings</Link>
        </div>
      </div>
      {err && <p className="hq-v123-warn">{err}</p>}
      {data && data.migration_ready === false && <p className="hq-v123-warn">The portal-payments migration isn’t applied yet.</p>}
      {!data ? <p className="hq-empty">Loading…</p> : data.error ? <p className="hq-empty">{data.error}</p> : list.length === 0 ? (
        <p className="hq-empty">No portal payments yet. Clients pay from “Make a Payment” in their portal once card payments, bank details or check instructions are set up in Settings › Client portal payments.</p>
      ) : (
        <div className="hq-v123-scroll">
          <table className="hq-table hq-v123-table">
            <thead><tr><th>When</th><th>Client</th><th className="hq-v123-hide-sm">Invoices</th><th className="r">Amount</th><th>Status</th><th className="hq-v123-actcol" /></tr></thead>
            <tbody>
              {list.map((p) => {
                const [label, tone] = statusOf(p);
                return (
                  <tr key={p.id}>
                    <td className="hq-v123-small">{stamp(p.created_at)}<div className="hq-v123-muted">{p.ref}{p.by ? ' · ' + p.by : ''}</div></td>
                    <td><Link to={'/clients/' + p.client_id}>{p.client_name}</Link>
                      <div className="hq-v123-muted hq-v123-small">{p.method === 'card' ? (p.card || 'Card') + (p.card_funding ? ' · ' + p.card_funding : '')
                        : (p.method === 'check' ? 'Check' + (p.sent_on ? ' · mailed ' : '') : p.method === 'wise' ? 'Wise' + (p.sent_on ? ' · paid ' : '') : 'Bank transfer (ACH)' + (p.sent_on ? ' · sent ' : '')) + (p.sent_on ? String(p.sent_on).slice(0, 10) : '')}</div></td>
                    <td className="hq-v123-hide-sm hq-v123-small">{p.invoices.map((i) => i.number + ' ' + money(i.amount, p.currency_code)).join(', ')}
                      {p.note && <div className="hq-v123-muted">“{p.note}”</div>}</td>
                    <td className="r">{money(p.method === 'card' ? p.charged : p.subtotal, p.currency_code)}
                      {p.fee_amount > 0 && <div className="hq-v123-muted hq-v123-small">{p.fee_refunded > 0 ? 'fee refunded (debit)' : 'incl. ' + money(p.fee_amount, p.currency_code) + ' fee'}</div>}</td>
                    <td><span className={'hq-pill hq-v124-st hq-v124-' + tone}>{label}</span>
                      {p.books_fee_invoice && <div className="hq-v123-muted hq-v123-small">fee invoice {p.books_fee_invoice}</div>}
                      {p.error && <div className="hq-v123-red hq-v123-small" style={{ fontWeight: 400, maxWidth: 320 }}>{p.error}</div>}</td>
                    <td className="hq-v123-actcol">
                      <div className="hq-v123-acts">
                        {p.method === 'card' && ['paid', 'record_failed'].includes(p.status) && (
                          <button type="button" className="hq-btn hq-v123-go" disabled={!!busy} onClick={() => act(p, 'retry')}>{busy === p.id + 'retry' ? 'Recording…' : 'Record in Books'}</button>
                        )}
                        {['reported', 'paid', 'record_failed'].includes(p.status) && (
                          <button type="button" className="hq-btn hq-btn-ghost" disabled={!!busy}
                            onClick={() => { if (window.confirm('Mark this payment as recorded? Only do this once it’s applied to the invoices in Zoho Books.')) act(p, 'mark-recorded'); }}>
                            Mark recorded
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
